import {
  useQueryClient,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { isActiveThreadStatus } from "@/shared/domain/agentThread/AgentThreadRecord";

/** ``live`` poll cadence while the agent is generating or running tools
 *  (``ROBOTO_TURN`` / ``CLIENT_TOOL_TURN``). Matches the admin thread viewer. */
export const ACTIVE_THREAD_POLL_INTERVAL_MS = 1500;

/** ``live`` poll cadence in every other status. These threads can still
 *  change: a freshly created thread sits in ``NOT_STARTED`` until the worker
 *  picks it up, and a ``USER_TURN`` thread gains messages whenever its owner
 *  continues it. */
export const IDLE_THREAD_POLL_INTERVAL_MS = 5000;

/** Query key of the record ``useAgentThread`` caches. Mutations that patch the
 *  thread (e.g. ``useUpdateThreadVisibility``) target the same entry. */
export function agentThreadQueryKey(threadId: string) {
  return ["agentThread", threadId] as const;
}

export interface UseAgentThreadParams {
  /**
   * Identifier of the agent thread to fetch. Required — the caller
   * must narrow ``threadId`` to a string (e.g. by branching on the
   * value from ``useParams``) before invoking this hook.
   */
  threadId: string;

  /**
   * Poll for as long as the caller is mounted and its tab is visible, at
   * ``ACTIVE_THREAD_POLL_INTERVAL_MS`` while the agent owes work and
   * ``IDLE_THREAD_POLL_INTERVAL_MS`` otherwise; polling never stops on its
   * own. For surfaces that show a thread they cannot drive, such as a
   * trigger-launched thread (owned by the org's service user) that its
   * launcher opens while it runs. Callers rendering the interactive
   * ``<AgentThread />`` should leave this off: that component already polls
   * under its own query key.
   *
   * @default false
   */
  live?: boolean;
}

/**
 * Fetches the full record for one agent thread.
 *
 * Suspends until the first response is available and throws to the nearest
 * ``<ErrorBoundary>`` if that load fails, so callers can keep the rendering
 * of the loaded state free of ``isLoading`` / ``error`` branching. A failed
 * refetch keeps serving the cached record; the next poll tries again.
 *
 * Polls only when ``live`` is set; every observer of the query key, ``live``
 * or not, receives the refreshed record.
 *
 * Once a record with a continuation token is cached, refetches go through
 * the delta endpoint and merge onto it, as ``useActiveAgentThread`` does, so
 * a poll costs the new messages rather than the whole conversation. The
 * delta carries no ``visibility``; ``useUpdateThreadVisibility`` cancels
 * in-flight polls and patches this key directly for that reason.
 *
 * The query key (:func:`agentThreadQueryKey`) is distinct from the
 * sidebar's ``useActiveAgentThread`` key so a detail page mounted on the same screen
 * as the sidebar does not contend with it on cache invalidation.
 */
export function useAgentThread({
  threadId,
  live = false,
}: UseAgentThreadParams): UseSuspenseQueryResult<AgentThreadRecord, Error> {
  const { agentThreadService } = useDomainServices();
  const queryClient = useQueryClient();
  return useSuspenseQuery<AgentThreadRecord, Error>({
    queryKey: agentThreadQueryKey(threadId),
    queryFn: ({ signal, queryKey }) => {
      const previous = queryClient.getQueryData<AgentThreadRecord>(queryKey);
      if (previous?.continuation_token) {
        return agentThreadService.updateAgentThreadFromDeltas(previous, {
          abortSignal: signal,
        });
      }
      return agentThreadService.getAgentThreadById(threadId, true, {
        abortSignal: signal,
      });
    },
    refetchInterval: live
      ? (query) =>
          isActiveThreadStatus(query.state.data?.status)
            ? ACTIVE_THREAD_POLL_INTERVAL_MS
            : IDLE_THREAD_POLL_INTERVAL_MS
      : false,
  });
}
